import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Briefcase,
  CalendarClock,
  ChevronDown,
  CreditCard,
  LogOut,
  Menu,
  TrendingUp,
  User,
  X,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";

interface NavbarProps {
  onAuthClick: (mode: "login" | "signup") => void;
  onLogout: () => void;
  onToggleSidebar: () => void;
  onOpenBusinessProfile: () => void;
}

const pricingLink = { label: "Pricing", path: "/pricing" };
const partnersLink = { label: "Partners", path: "/partners" };
const freePlanMoreLinks = [pricingLink, partnersLink];

const navLinks = () => [
  { label: "LLM Visibility", path: "/llm-visibility" },
  { label: "Persona Visibility", path: "/persona" },
  { label: "OTA vs Direct", path: "/ota-direct" },
  { label: "AI Perception", path: "/perception" },
  partnersLink,
  pricingLink,
];

const moreSolutionsLinks = [
  { label: "AI Readiness", path: "/ai-readiness" },
  { label: "AI Reputation", path: "/ai-reputation" },
  { label: "Google Analytics", path: "/google-analytics" },
  { label: "ROI Calculator", path: "/calculator" },
];

const Navbar = ({ onAuthClick, onLogout, onToggleSidebar, onOpenBusinessProfile }: NavbarProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMoreSolutions, setShowMoreSolutions] = useState(false);
  const moreSolutionsRef = useRef<HTMLDivElement>(null);

  // Close the "More" menu when clicking outside it. Using a mousedown listener
  // (rather than the button's onBlur) keeps the dropdown mounted through a menu
  // item's own click, so navigating from it isn't swallowed.
  useEffect(() => {
    if (!showMoreSolutions) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (moreSolutionsRef.current && !moreSolutionsRef.current.contains(event.target as Node)) {
        setShowMoreSolutions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMoreSolutions]);

  const isFreePlan = user?.subscriptionPlan === "free";
  const configuredNavLinks = navLinks();
  const displayedNavLinks = isFreePlan
    ? configuredNavLinks.filter((link) => !freePlanMoreLinks.includes(link))
    : configuredNavLinks;
  const displayedMoreSolutionsLinks = isFreePlan
    ? [...moreSolutionsLinks, ...freePlanMoreLinks]
    : moreSolutionsLinks;

  return (
    <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl shadow-sm">
      <div className="flex justify-between items-center px-8 h-16 max-w-8xl mx-auto font-headline tracking-tight">
        <Link
          to="/"
          className="flex items-center gap-2 text-xl font-bold tracking-tighter text-on-surface"
        >
          <img src="/logo.svg" alt="AppearInAISearch" className="w-12 h-12" /> AppearInAISearch
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-5">
          <Link
            to="/audit"
            className={`text-sm transition-colors ${
              location.pathname.startsWith("/audit")
                ? "text-primary font-bold border-b-2 border-primary pb-1"
                : "text-on-surface/70 hover:text-primary font-medium"
            }`}
          >
            AI Audit
          </Link>
          {displayedNavLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm transition-colors ${
                location.pathname.startsWith(link.path)
                  ? "text-primary font-bold border-b-2 border-primary pb-1"
                  : "text-on-surface/70 hover:text-primary font-medium"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="relative" ref={moreSolutionsRef}>
            <button
              onClick={() => setShowMoreSolutions(!showMoreSolutions)}
              className={`flex items-center gap-1 text-sm transition-colors ${
                displayedMoreSolutionsLinks.some((l) => location.pathname.startsWith(l.path))
                  ? "text-primary font-bold border-b-2 border-primary pb-1"
                  : "text-on-surface/70 hover:text-primary font-medium"
              }`}
            >
              More
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showMoreSolutions && (
              // Pressing a menu item would otherwise move focus off the trigger, and the
              // trigger's onBlur unmounts this menu 150ms later — fast enough to remove the
              // link before mouseup, swallowing the click. Suppressing mousedown's default
              // (the focus transfer) keeps the trigger focused so the timer never starts;
              // the Link's own onClick still closes the menu.
              <div
                onMouseDown={(event) => event.preventDefault()}
                className="absolute left-0 top-full mt-2 w-48 bg-surface-container-lowest shadow-cloud rounded-xl py-2 z-50 ring-1 ring-outline-variant/20"
              >
                {displayedMoreSolutionsLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setShowMoreSolutions(false)}
                    className={`block px-4 py-2 text-sm transition-colors ${
                      location.pathname.startsWith(link.path)
                        ? "text-primary font-semibold bg-surface-container-high"
                        : "text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user && (
            <button
              onClick={onToggleSidebar}
              className="text-sm text-on-surface-variant hover:text-primary transition-colors font-medium"
            >
              View Reports
            </button>
          )}
          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center gap-2 text-on-surface hover:text-primary transition-colors font-medium text-sm"
              >
                <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center">
                  <User className="w-4 h-4 text-on-primary" />
                </div>
                <span>{user.getDisplayName()}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showUserDropdown && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-surface-container-lowest shadow-cloud rounded-xl py-2 z-50 ring-1 ring-outline-variant/20">
                  <div className="px-4 py-3 border-b border-surface-container-high">
                    <div className="text-sm font-semibold text-on-surface">
                      {user.subscriptionPlan}
                    </div>
                    <div className="text-xs text-on-surface-variant">
                      {user.subscriptionPlan !== "free"
                        ? "Active subscription"
                        : "No active subscription"}
                    </div>
                  </div>
                  <Link
                    to="/subscription"
                    onClick={() => setShowUserDropdown(false)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    Subscription
                  </Link>
                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      onOpenBusinessProfile();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-colors"
                  >
                    <Briefcase className="w-4 h-4" />
                    Business Profile
                  </button>
                  <Link
                    to="/scheduled-reports"
                    onClick={() => setShowUserDropdown(false)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-colors"
                  >
                    <CalendarClock className="w-4 h-4" />
                    Scheduled Reports
                  </Link>
                  <Link
                    to="/trends"
                    onClick={() => setShowUserDropdown(false)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-colors"
                  >
                    <TrendingUp className="w-4 h-4" />
                    View Trends
                  </Link>
                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      onLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                onClick={() => onAuthClick("signup")}
                className="bg-gradient-to-br from-primary to-primary-container text-on-primary px-6 py-2 rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity active:scale-95 duration-150"
              >
                Get Started
              </button>
              <button
                onClick={() => onAuthClick("login")}
                className="text-on-surface-variant hover:text-primary text-sm font-semibold transition-colors"
              >
                Login
              </button>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-on-surface"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-surface-container-lowest/95 backdrop-blur-xl border-t border-surface-container-high px-8 py-6 space-y-4">
          <Link
            to="/audit"
            onClick={() => setMobileMenuOpen(false)}
            className="block text-sm font-medium text-on-surface hover:text-primary transition-colors"
          >
            AI Audit
          </Link>
          {displayedNavLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-medium text-on-surface-variant hover:text-primary transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <div className="text-xs font-semibold text-on-surface-variant/50 uppercase tracking-widest pt-1">
            More
          </div>
          {displayedMoreSolutionsLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-medium text-on-surface-variant hover:text-primary transition-colors pl-2"
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-4 border-t border-surface-container-high space-y-3">
            {user ? (
              <>
                <Link
                  to="/subscription"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-sm text-on-surface-variant hover:text-primary transition-colors"
                >
                  Subscription
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onOpenBusinessProfile();
                  }}
                  className="block text-sm text-on-surface-variant hover:text-primary transition-colors"
                >
                  Business Profile
                </button>
                <Link
                  to="/scheduled-reports"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-sm text-on-surface-variant hover:text-primary transition-colors"
                >
                  Scheduled Reports
                </Link>
                <Link
                  to="/trends"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-sm text-on-surface-variant hover:text-primary transition-colors"
                >
                  View Trends
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout();
                  }}
                  className="block text-sm text-on-surface-variant hover:text-primary transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onAuthClick("signup");
                  }}
                  className="block w-full bg-gradient-to-br from-primary to-primary-container text-on-primary px-6 py-2.5 rounded-xl font-semibold text-sm text-center"
                >
                  Get Started
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onAuthClick("login");
                  }}
                  className="block w-full text-center text-sm text-on-surface-variant hover:text-primary transition-colors"
                >
                  Login
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="bg-surface-container-high/50 h-[1px] w-full absolute bottom-0" />
    </header>
  );
};

export default Navbar;
